import { isEqual, cloneDeep } from 'lodash';

/**
 * UnsavedChangesGuard Mixin
 *
 * Provides unsaved changes detection and navigation guard functionality
 * to prevent users from accidentally losing work when navigating away.
 *
 * Usage:
 * 1. Add to component's mixins array
 * 2. Implement getTrackableState() method to return state to track
 * 3. Call captureInitialState() after data loads
 * 4. Call resetInitialState() after successful save
 * 5. Add ConfirmNavigationDialog to template
 *
 * @example
 * export default {
 *   mixins: [UnsavedChangesGuard],
 *   methods: {
 *     getTrackableState() {
 *       return { quoteData: this.quoteData };
 *     },
 *     async loadQuote() {
 *       this.quoteData = await fetchQuote();
 *       this.$nextTick(() => this.captureInitialState());
 *     },
 *     async saveQuote() {
 *       await saveToAPI();
 *       this.$nextTick(() => this.resetInitialState());
 *     }
 *   }
 * }
 */
export default {
	data() {
		return {
			// Initial state snapshot for comparison
			initialState: null as any,

			// Flag to enable/disable warning (can be disabled programmatically)
			showNavigationWarning: true,

			// Dialog visibility
			showUnsavedChangesDialog: false,

			// Pending navigation resolver
			pendingNavigation: null as ((proceed: boolean) => void) | null,

			// Loading state for save-and-leave operation
			savingBeforeLeave: false,
		};
	},

	computed: {
		/**
		 * Determines if the component has unsaved changes
		 * by comparing current state to initial state
		 */
		hasUnsavedChanges(): boolean {
			if (!this.initialState) {
				return false;
			}

			const currentState = (this as any).getTrackableState();
			const hasChanges = !isEqual(currentState, this.initialState);

			return hasChanges;
		},
	},

	methods: {
		/**
		 * MUST be implemented by component using this mixin
		 * Returns the state object to track for changes
		 */
		getTrackableState(): object {
			throw new Error(
				'Component must implement getTrackableState() method'
			);
		},

		/**
		 * Captures the initial state snapshot
		 * Call this after data is loaded
		 */
		captureInitialState(): void {
			const state = (this as any).getTrackableState();
			this.initialState = cloneDeep(state);
		},

		/**
		 * Resets the initial state to current state
		 * Call this after successful save
		 */
		resetInitialState(): void {
			(this as any).captureInitialState();
		},

		/**
		 * Checks for unsaved changes
		 */
		checkForChanges(): boolean {
			return (this as any).hasUnsavedChanges;
		},

		/**
		 * Shows the confirmation dialog
		 * Returns a promise that resolves to true (proceed) or false (cancel)
		 */
		async confirmNavigation(): Promise<boolean> {
			return new Promise((resolve) => {
				this.pendingNavigation = resolve;
				this.showUnsavedChangesDialog = true;
			});
		},

		/**
		 * User chose to stay on page
		 */
		handleNavigationStay(): void {
			this.showUnsavedChangesDialog = false;
			if (this.pendingNavigation) {
				this.pendingNavigation(false);
				this.pendingNavigation = null;
			}
		},

		/**
		 * MUST be overridden by component to enable "Save & Continue"
		 * Should perform the save operation (same as the normal save button)
		 */
		async saveBeforeLeave(): Promise<void> {
			throw new Error(
				'Component must implement saveBeforeLeave() method to use Save & Continue'
			);
		},

		/**
		 * User chose to save and then leave page
		 * Calls the component's saveBeforeLeave(), checks if save succeeded,
		 * then either navigates or stays on validation failure
		 */
		async handleNavigationSaveAndLeave(): Promise<void> {
			this.savingBeforeLeave = true;
			try {
				await (this as any).saveBeforeLeave();

				// Check showNavigationWarning to determine if save succeeded.
				// Save methods set showNavigationWarning = false on success
				// and leave it true on validation failure or early return.
				// This is more reliable than checking hasUnsavedChanges, which
				// can be stale due to async data reloads after resetInitialState.
				if (!this.showNavigationWarning) {
					// Save succeeded — proceed with navigation
					this.showUnsavedChangesDialog = false;
					if (this.pendingNavigation) {
						this.pendingNavigation(true);
						this.pendingNavigation = null;
					}
				} else {
					// Save failed (validation error) — stay on page
					this.showUnsavedChangesDialog = false;
					if (this.pendingNavigation) {
						this.pendingNavigation(false);
						this.pendingNavigation = null;
					}
				}
			} catch {
				// Save threw — stay on page
				this.showUnsavedChangesDialog = false;
				if (this.pendingNavigation) {
					this.pendingNavigation(false);
					this.pendingNavigation = null;
				}
			} finally {
				this.savingBeforeLeave = false;
			}
		},

		/**
		 * User chose to discard changes and leave page
		 */
		handleNavigationLeave(): void {
			this.showUnsavedChangesDialog = false;
			this.showNavigationWarning = false; // Disable guard for this navigation
			if (this.pendingNavigation) {
				this.pendingNavigation(true);
				this.pendingNavigation = null;
			}
		},

		/**
		 * Browser beforeunload handler
		 * Shows browser's native warning when closing tab/window
		 */
		handleBeforeUnload(event: BeforeUnloadEvent): void {
			if ((this as any).hasUnsavedChanges && this.showNavigationWarning) {
				event.preventDefault();
				event.returnValue = ''; // Required for Chrome
			}
		},

		/**
		 * Check if navigating to a related view of the same entity
		 * Returns true if we're just switching views (payments, print, etc.) within the same parent
		 */
		isNavigatingToRelatedView(from: any, to: any): boolean {
			// Extract base paths and IDs
			const fromPath = from.path;
			const toPath = to.path;

			// Check for quotes navigation: /quotes/123 <-> /quotes/123/payments <-> /quotes/123/print
			const quoteMatch = fromPath.match(/^\/sales-orders\/quotes\/(\d+)/);
			if (quoteMatch) {
				const quoteId = quoteMatch[1];
				// Allow navigation within same quote (main view, payments, print)
				return toPath.startsWith(`/sales-orders/quotes/${quoteId}`);
			}

			// Check for orders navigation: /orders/123 <-> /orders/123/payments <-> /orders/123/print, etc.
			const orderMatch = fromPath.match(/^\/sales-orders\/orders\/(\d+)/);
			if (orderMatch) {
				const orderId = orderMatch[1];
				// Allow navigation within same order (main view, payments, print, status, etc.)
				return toPath.startsWith(`/sales-orders/orders/${orderId}`);
			}

			// Check for sample orders navigation: /sample-orders/123 <-> /sample-orders/123/payments, etc.
			const sampleOrderMatch = fromPath.match(/^\/sales-orders\/sample-orders\/(\d+)/);
			if (sampleOrderMatch) {
				const orderId = sampleOrderMatch[1];
				// Allow navigation within same sample order (main view, payments, print, status, etc.)
				return toPath.startsWith(`/sales-orders/sample-orders/${orderId}`);
			}

			// Not a recognized entity navigation pattern
			return false;
		},
	},

	/**
	 * Vue Router navigation guard
	 * Intercepts navigation attempts and shows confirmation dialog if unsaved changes exist
	 */
	async beforeRouteLeave(to: any, from: any, next: any): Promise<void> {
		// Check if navigating within the same entity (e.g., /quotes/123 -> /quotes/123/payments)
		const isSameEntity = this.isNavigatingToRelatedView(from, to);
		const isNavigatingToChildView = isSameEntity && to.path.length > from.path.length;
		const isNavigatingFromChildView = isSameEntity && to.path.length < from.path.length;

	// Check for unsaved changes in this component
	let hasUnsaved = (this as any).hasUnsavedChanges;
	let warningEnabled = this.showNavigationWarning;

	// Also check child components (like SalesPayments) for unsaved changes
	// Child components rendered with v-if don't have their own beforeRouteLeave
	if ((this as any).$refs?.salesPayments) {
		const salesPayments = (this as any).$refs.salesPayments;
		const childHasUnsaved = salesPayments.hasUnsavedChanges || false;
		const childWarningEnabled = salesPayments.showNavigationWarning !== false;
		if (childHasUnsaved) {
			hasUnsaved = true;
			// If child has unsaved changes, also check if child's warning is enabled
			warningEnabled = childWarningEnabled;
		}
	}

	// If no unsaved changes or warning disabled, allow navigation
	if (!hasUnsaved || !warningEnabled) {
		next();
		return;
	}

		if (isNavigatingToChildView) {
			// Allow navigation from main view to child views (payments, print, etc.)
			// Data is preserved in the parent component
		// Re-enable navigation warning since user may make changes in child view
		this.showNavigationWarning = true;
			next();
			return;
		}

		if (isNavigatingFromChildView) {
			// When navigating from child view back to parent (e.g., payments -> main quote view),
			// only check the child's unsaved changes, not the parent's.
			// The parent will show its own warning when user leaves the entity entirely.
			const salesPayments = (this as any).$refs?.salesPayments;
			const childHasUnsaved = salesPayments?.hasUnsavedChanges || false;

			const childWarningEnabled = salesPayments?.showNavigationWarning !== false;

			if (childHasUnsaved && childWarningEnabled) {
				// Child has unsaved changes and warning is enabled, show warning
				const shouldProceed = await (this as any).confirmNavigation();
				if (shouldProceed) {
					next();
				} else {
					next(false);
				}
			} else {
				// Child is saved or warning is disabled, allow navigation back to parent
				next();
			}
			return;
		}

		// Show confirmation dialog for all other navigation
		const shouldProceed = await (this as any).confirmNavigation();

		if (shouldProceed) {
			next();
		} else {
			next(false);
		}
	},

	mounted(): void {
		// Add browser close warning
		window.addEventListener('beforeunload', (this as any).handleBeforeUnload);
	},

	beforeDestroy(): void {
		// Clean up
		window.removeEventListener('beforeunload', (this as any).handleBeforeUnload);
	},
};
